import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { SITE, waLink } from "@/lib/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-background/95 backdrop-blur-md border-b border-brand-line shadow-[0_1px_20px_rgba(21,30,28,0.05)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="group flex items-center gap-3">
          <span
            aria-label="ENT logo"
            className="inline-flex items-center rounded-lg bg-white px-2 py-1 border border-brand-line"
          >
            <img src="/ent-logo.png" alt="ENT logo — ear, nose, throat" className="h-9 w-auto" />
          </span>
          <span className="leading-tight">
            <span className="block font-heading font-bold text-xl text-brand-ink">{SITE.doctorName}</span>
            <span className="block text-[10px] tracking-[0.22em] uppercase text-brand-muted">
              ENT Specialist · Varanasi
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {SITE.nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm tracking-wide transition-colors duration-200 hover:text-brand-pine ${
                  isActive ? "text-brand-pine" : "text-brand-ink"
                }`
              }
            >
              {({ isActive }) => (
                <span className="inline-flex flex-col">
                  {item.label}
                  <span
                    className={`h-px mt-1 bg-brand-pine transition-[width] duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="header-call-link"
            className="inline-flex items-center gap-2 text-sm text-brand-ink hover:text-brand-pine transition-colors"
          >
            <Phone className="w-4 h-4" />
            {SITE.phoneDisplay}
          </a>
          <a
            href={waLink(`Hello, I would like to book an appointment with ${SITE.doctorName}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-btn"
            className="inline-flex items-center rounded-full bg-brand-teal px-6 py-2.5 text-sm text-brand-ink transition-[background-color,transform] duration-200 hover:bg-brand-tealHover hover:scale-[1.03]"
          >
            Book Appointment
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden grid place-items-center w-11 h-11 rounded-full border border-brand-line bg-brand-surface text-brand-ink"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-background border-b border-brand-line"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {SITE.nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 text-lg font-heading border-b border-brand-line ${
                      isActive ? "text-brand-pine" : "text-brand-ink"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href={waLink(`Hello, I would like to book an appointment with ${SITE.doctorName}.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-btn"
                className="mt-4 inline-flex justify-center rounded-full bg-brand-teal px-6 py-3 text-sm text-brand-ink"
              >
                Book Appointment
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
